import React from 'react';
import { Button } from './Button';
import { motion } from 'framer-motion';
import { Target, Users, Clock, ArrowDownIcon } from 'lucide-react';
interface HeroProps {
  onApplyClick: () => void;
}
export function Hero({
  onApplyClick
}: HeroProps) {
  const scrollToHowItWorks = () => {
    const element = document.getElementById('how-it-works');
    element?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative overflow-hidden" data-id="element-95">
      {/* Background effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] bg-gradient-to-b from-mint-500/[0.07] via-wave-500/[0.03] to-transparent rounded-[100%] blur-[100px] -z-10" data-id="element-96" />
      <div className="absolute top-20 right-[10%] w-[300px] h-[300px] bg-wave-500/[0.05] rounded-full blur-[80px] -z-10" data-id="element-97" />

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20 lg:pt-48 lg:pb-28" data-id="element-98">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center" data-id="element-99">
          <motion.div initial={{
          opacity: 0,
          y: 28
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} data-id="element-100">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-gradient-to-r from-mint-500/10 to-wave-500/10 border border-mint-500/20 mb-8" data-id="element-101">
              <span className="relative flex h-2 w-2" data-id="element-102">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-mint-400 opacity-75" data-id="element-103"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-mint-500" data-id="element-104"></span>
              </span>
              <span className="text-sm font-semibold text-mint-400" data-id="element-105">
                Creator Lab — Now accepting applications
              </span>
            </motion.div>

            <h1 className="text-5xl lg:text-7xl xl:text-[5.25rem] font-extrabold leading-[1.05] tracking-tight text-white mb-8" data-id="element-106">
              Your fans want more.
            </h1>

            <p className="text-xl lg:text-2xl text-slate-300 mb-10 leading-relaxed max-w-xl font-medium" data-id="element-107">
              Stop settling for polls and comments. Pulse Markets give your
              audience a real stake in what you do next.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-10" data-id="element-108">
              <Button variant="primary" onClick={onApplyClick} className="w-full sm:w-auto text-lg px-8 py-4 shadow-xl shadow-mint-500/15 hover:shadow-mint-500/25" data-id="element-109">
                Apply to the Creator Lab
              </Button>
              <button onClick={scrollToHowItWorks} className="w-full sm:w-auto flex items-center justify-center gap-2 text-lg px-8 py-4 text-slate-300 hover:text-white transition-colors font-medium group" data-id="element-110">
                How it works
                <ArrowDownIcon className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" data-id="element-111" />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-400" data-id="element-112">
              <span data-id="element-113">4 weeks</span>
              <span className="w-1 h-1 rounded-full bg-slate-700" data-id="element-114" />
              <span data-id="element-115">Fully remote</span>
              <span className="w-1 h-1 rounded-full bg-slate-700" data-id="element-116" />
              <span className="text-mint-400 font-semibold" data-id="element-117">
                $2,000 stipend
              </span>
              <span className="w-1 h-1 rounded-full bg-slate-700" data-id="element-118" />
              <span data-id="element-119">5 creators</span>
            </div>
          </motion.div>

          {/* Hero Visual — Premium Market Card */}
          <motion.div initial={{
          opacity: 0,
          y: 28
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="relative lg:ml-auto w-full max-w-[420px]" data-id="element-120">
            {/* Card glow */}
            <div className="absolute -inset-4 bg-gradient-to-br from-mint-500/10 via-transparent to-wave-500/10 rounded-3xl blur-2xl -z-10" data-id="element-121" />

            <div className="rounded-2xl bg-gradient-to-b from-slate-800/60 to-slate-900/40 p-[1px]" data-id="element-122">
              <div className="rounded-2xl bg-gradient-to-b from-navy-900 to-navy-950 p-7" data-id="element-123">
                {/* Card Header */}
                <div className="flex items-center justify-between mb-7" data-id="element-124">
                  <div className="flex items-center gap-3" data-id="element-125">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-mint-500 to-wave-500 flex items-center justify-center text-navy-950 text-sm font-bold" data-id="element-126">
                      CX
                    </div>
                    <div data-id="element-127">
                      <div className="text-sm font-bold text-white" data-id="element-128">
                        @CreatorX
                      </div>
                      <div className="text-xs text-slate-500 font-medium" data-id="element-129">
                        Pulse Market
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-mint-500/10 border border-mint-500/20" data-id="element-130">
                    <span className="w-1.5 h-1.5 rounded-full bg-mint-500 animate-pulse" data-id="element-131"></span>
                    <span className="text-[11px] font-bold text-mint-400 uppercase tracking-wide" data-id="element-132">
                      Live
                    </span>
                  </div>
                </div>

                {/* Goal */}
                <div className="flex items-center gap-3 mb-4 px-4 py-3 rounded-xl bg-gradient-to-r from-mint-500/[0.06] to-wave-500/[0.04] border border-slate-700/40" data-id="element-133">
                  <Target className="w-4 h-4 text-mint-500 flex-shrink-0" data-id="element-134" />
                  <span className="text-sm font-semibold text-slate-200" data-id="element-135">
                    Reach 100K subscribers in 30 days
                  </span>
                </div>

                {/* Description */}

                {/* Question */}
                <h3 className="text-lg font-bold text-white mb-5 leading-snug" data-id="element-136">
                  Which fan giveaway gets us to 100K subscribers in the next 30
                  days?
                </h3>

                {/* Options */}
                <div className="space-y-2.5 mb-7" data-id="element-137">
                  {/* Option A — Selected */}
                  <div className="relative rounded-xl p-4 flex justify-between items-center bg-mint-500/[0.08] border border-mint-500/30 overflow-hidden" data-id="element-138">
                    <div className="flex flex-col relative z-10" data-id="element-139">
                      <span className="font-semibold text-white text-sm" data-id="element-140">
                        1-on-1 session with us
                      </span>
                      <span className="text-[11px] text-mint-400 font-bold mt-1" data-id="element-141">
                        YOUR TAKE
                      </span>
                    </div>
                    <span className="text-lg font-bold text-mint-400 tabular-nums relative z-10" data-id="element-142">
                      38%
                    </span>
                    <div className="absolute bottom-0 left-0 h-1 w-full bg-navy-950/50" data-id="element-143">
                      <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: '38%'
                    }} transition={{
                      duration: 1,
                      delay: 0.5,
                      ease: 'easeOut'
                    }} className="h-full bg-gradient-to-r from-mint-500 to-mint-400 rounded-full" data-id="element-144" />
                    </div>
                  </div>

                  {/* Option B */}
                  <div className="relative rounded-xl p-4 flex justify-between items-center bg-slate-800/20 border border-slate-700/30 overflow-hidden" data-id="element-145">
                    <span className="font-medium text-slate-300 text-sm" data-id="element-146">
                      Small group training session
                    </span>
                    <span className="text-sm font-bold text-slate-500 tabular-nums" data-id="element-147">
                      28%
                    </span>
                    <div className="absolute bottom-0 left-0 h-1 w-full bg-navy-950/50" data-id="element-148">
                      <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: '28%'
                    }} transition={{
                      duration: 1,
                      delay: 0.6,
                      ease: 'easeOut'
                    }} className="h-full bg-slate-700 rounded-full" data-id="element-149" />
                    </div>
                  </div>

                  {/* Option C */}
                  <div className="relative rounded-xl p-4 flex justify-between items-center bg-slate-800/20 border border-slate-700/30 overflow-hidden" data-id="element-150">
                    <span className="font-medium text-slate-300 text-sm" data-id="element-151">
                      Exclusive gear bundle
                    </span>
                    <span className="text-sm font-bold text-slate-500 tabular-nums" data-id="element-152">
                      21%
                    </span>
                    <div className="absolute bottom-0 left-0 h-1 w-full bg-navy-950/50" data-id="element-153">
                      <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: '21%'
                    }} transition={{
                      duration: 1,
                      delay: 0.7,
                      ease: 'easeOut'
                    }} className="h-full bg-slate-700 rounded-full" data-id="element-154" />
                    </div>
                  </div>

                  {/* Option D */}
                  <div className="relative rounded-xl p-4 flex justify-between items-center bg-slate-800/20 border border-slate-700/30 overflow-hidden" data-id="element-155">
                    <span className="font-medium text-slate-300 text-sm" data-id="element-156">
                      Behind-the-scenes filming day
                    </span>
                    <span className="text-sm font-bold text-slate-500 tabular-nums" data-id="element-157">
                      13%
                    </span>
                    <div className="absolute bottom-0 left-0 h-1 w-full bg-navy-950/50" data-id="element-158">
                      <motion.div initial={{
                      width: 0
                    }} animate={{
                      width: '13%'
                    }} transition={{
                      duration: 1,
                      delay: 0.8,
                      ease: 'easeOut'
                    }} className="h-full bg-slate-700 rounded-full" data-id="element-159" />
                    </div>
                  </div>
                </div>

                {/* Footer Stats */}
                <div className="flex items-center justify-between text-xs pt-5 border-t border-slate-800/40" data-id="element-160">
                  <div className="flex items-center gap-1.5 text-slate-400" data-id="element-161">
                    <Users className="w-3.5 h-3.5" data-id="element-162" />
                    <span className="font-medium" data-id="element-163">2.8k Takes</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-mint-400 font-medium" data-id="element-164">
                    <Clock className="w-3.5 h-3.5" data-id="element-165" />
                    <span data-id="element-166">2d left</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}